'use client';

import SectionCard from '@/app/(website)/components/SectionCard';
import SectionHeader from '@/app/(website)/components/SectionHeader';
import CheckCircle from '@/assets/icons/CheckCircle.svg';
import Clock from '@/assets/icons/Clock.svg';
import Flash from '@/assets/icons/Flash.svg';
import Rocket from '@/assets/icons/Rocket.svg';
import { useEffect, useRef, useState } from 'react';

export default function HighlightsCaf() {
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
      },
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      if (gridRef.current) {
        observer.unobserve(gridRef.current);
      }
    };
  }, []);

  return (
    <div
      id="highlights"
      className="flex flex-col items-center gap-10 py-16 px-8"
    >
      <SectionHeader
        badgeText="Funzionalità"
        title={
          <>
            Riduci il tempo operativo,{' '}
            <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
              aumenta
            </span>{' '}
            la qualità delle risposte date ai tuoi clienti
          </>
        }
        subtitle="Con Normo.ai, l'operatore del CAF ha un supporto concreto nella consultazione delle guide fiscali, nell'analisi dei documenti portati dai cittadini e nella riduzione del rischio di errori che potrebbero compromettere il visto di conformità."
      />
      <div
        className="grid grid-cols-1 md:grid-cols-2 max-w-[56.625rem] gap-8"
        ref={gridRef}
      >
        <SectionCard
          text="Ottieni risposte articolate basate sulla Normativa Italiana ed Europea, sulla Giurisprudenza Tributaria di merito e di Legittimità, e sulla Prassi ufficiale di Agenzia delle Entrate, INPS e Ministero del Lavoro (Circolari, Interpelli e Linee Guida)."
          title="Risposte con fonti rilevanti"
          icon={<Flash width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Resta allineato all'evoluzione di leggi e decreti senza dover inseguire manualmente ogni novità: l'intelligenza artificiale aggiorna ogni giorno il suo database per garantirti risposte basate sulla prassi e sulla giurisprudenza più recente."
          title="Fonti aggiornate quotidianamente"
          icon={<Clock width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Normo.ai supporta CAF e Centri Servizi nell'analisi della documentazione dei contribuenti e nella produzione di bozze operative per la gestione delle pratiche più ricorrenti. Può assistere nella compilazione e verifica dei modelli dichiarativi consultando rapidamente istruzioni ministeriali, normativa e prassi rilevanti. Inoltre, supporta la gestione dei contratti di lavoro: dall'analisi dell'inquadramento secondo il CCNL, calcoli, fino alla generazione della lettera di assunzione."
          title="Analisi, redazione e revisione documentale"
          icon={<CheckCircle width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Non uno strumento limitato a un singolo modulo, ma un supporto unico quando nella situazione del contribuente si intrecciano profili diversi: dal calcolo dell'ISEE patrimoniale alle detrazioni sul 730, fino ai riflessi previdenziali INPS e ai bonus sociali."
          title="Gestione trasversale del cittadino, non a silos"
          icon={<Rocket width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
      </div>
    </div>
  );
}
